import React, { useEffect } from "react";
import "./App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { SnackbarProvider } from "./context/SnackbarContext";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store/store";
import { injectGlobalStyles, DEFAULT_SETTINGS } from "./utils/themeUtils";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { AWSAuthProvider } from "./contexts/AWSAuthContext";
import LandingPage from "./components/LandingPage";
import MultiTabWebsite from "./components/MultiTabWebsite";
import Login from "./components/Auth/Login";
import ConsentPage from "./components/Auth/ConsentPage";
import UnderConstruction from "./components/UnderConstruction";
// EndUserDashboard is now rendered via DashboardRouter at /central-dashboard
import DocumentLibrary from "./components/DocumentLibrary";
import AutomatedRiskManagement from "./components/AutomatedRiskManagement";
import AutomatedThreatResponse from "./components/AutomatedThreatResponse";
import VaptModule from "./components/VAPT";
import CentralDashboard from "./components/CentralDashboard";
import AIAgentPage from "./components/AIAgentPage";
import DashboardRouter from "./components/CustomerAdmin/DashboardRouter";
import AutomateCompliance from "./components/CustomerAdmin/AutomateCompliance";
import Ai_CSPM from "./components/Ai_CSPM";
import VirtualSOC from "./components/SOC";
import AiCRM from "./components/AiCRM/";
import AIAssistant from "./components/AIAssistant";
import AssetManagement from "./components/AssetManagement/";
//import TeamCollaboration from "./components/TeamCollaboration";
import Ai_LMS from "./components/Ai_LMS/Ai_LMS";
import AiAnalytics from "./components/Reports";
import RiskRegister from "./components/RiskRegister";
import ExternalIntegrations from "./components/ExternalIntegrations";
import AdvancedAI from "./components/AdvancedAI";
import ProtectedRoute from "./components/ProtectedRoute";
import TPRM from "./components/TPRM";
import VendorAssessmentPage from "./components/TPRM/pages/VendorAssessmentPage";
import IntegrationAssessment from "./components/IntegrationAssessment";
import InteractiveDashboard from "./components/InteractiveDashboard";
import EnhancedDashboard from "./components/EnhancedDashboard";

// New GRC Components
import SecurityDashboard from "./components/SecurityDashboard";
import ComplianceDashboard from "./components/TrustCenter/";
import RiskManagement from "./components/RiskManagement";
import CompanyGuard from "./components/CompanySelection";
import Administration from "./components/Administration";
import PlatformSettings from "./components/Administration/PlatformSettings";
import { useAuth as useAuthHook } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
import IncidentReporting from "./components/IncidentReporting/index";
import EndUserIncidentReporting from "./components/IncidentReporting/EndUserIncidentReporting";
import AssetDetailsPage from "./components/AssetManagement/InventoryTab/AssetDetailsPage";
import TeamCollaboration from "./components/Team_collabration";
import AI_DSPM from "./components/AI_DSPM/index";

// Bridge: reads user after auth context is initialised and injects NotificationProvider
const NotificationBridge = ({ children }) => {
  const { user } = useAuthHook();
  return <NotificationProvider user={user}>{children}</NotificationProvider>;
};

const IncidentReportingWrapper = () => {
  const { isEndUser, user } = useAuthHook();

  if (isEndUser) {
    return <EndUserIncidentReporting />;
  }
  return <IncidentReporting />;
};

function App() {
  useEffect(() => {
    const initializePlatformSettings = () => {
      const defaultSettings = {
        appearance: {
          theme: "light",
          primaryColor: "#26256b",
          layoutStyle: "modern",
          fontFamily: "Inter",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: 1.5,
          letterSpacing: 0,
          borderRadius: 8,
        },
        layout: {
          sidebarWidth: 224,
          headerHeight: 64,
          contentPadding: 24,
          cardBorderRadius: 8,
          shadowIntensity: "medium",
        },
      };

      // Load saved settings or use defaults
      const savedSettings = localStorage.getItem("platformSettings");
      const settings = savedSettings
        ? JSON.parse(savedSettings)
        : defaultSettings;

      // Merge with defaults to ensure all properties exist
      const mergedSettings = {
        appearance: {
          ...defaultSettings.appearance,
          ...(settings.appearance || {}),
        },
        layout: { ...defaultSettings.layout, ...(settings.layout || {}) },
      };

      // Force update sidebarWidth to new default if it was the old default
      if (mergedSettings.layout.sidebarWidth === 256) {
        mergedSettings.layout.sidebarWidth = 224;
        localStorage.setItem(
          "platformSettings",
          JSON.stringify(mergedSettings),
        );
      }

      // Inject global styles
      injectGlobalStyles(mergedSettings);
    };

    initializePlatformSettings();

    // Listen for storage changes to sync across tabs
    const handleStorageChange = (e) => {
      if (e.key === "platformSettings" && e.newValue) {
        const settings = JSON.parse(e.newValue);
        injectGlobalStyles(settings);
      }
    };

    // Listen for theme changes in same tab (from Layout.js)
    const handleThemeChange = () => {
      initializePlatformSettings();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("themeChanged", handleThemeChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("themeChanged", handleThemeChange);
    };
  }, []);

  return (
    <div className="App">
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <ScrollToTop />
            <AWSAuthProvider>
              <AuthProvider>
                <ThemeProvider>
                  <SnackbarProvider>
                    <NotificationBridge>
                      <CompanyGuard>
                        <Routes>
                          {/* Public Routes */}
                          <Route path="/" element={<Login />} />
                          <Route path="/landing" element={<LandingPage />} />
                          <Route
                            path="/under-construction"
                            element={<UnderConstruction />}
                          />
                          <Route path="/consent" element={<ConsentPage />} />

                          {/* Multi-Tab Website Routes */}
                          <Route
                            path="/website"
                            element={<MultiTabWebsite />}
                          />
                          <Route
                            path="/website/*"
                            element={<MultiTabWebsite />}
                          />

                          <Route path="/login" element={<Login />} />
                          <Route
                            path="/vendor-assessment"
                            element={<VendorAssessmentPage />}
                          />

                          {/* Protected Routes with Module-based Access Control */}
                          {/* Legacy route redirects */}
                          <Route
                            path="/dashboard"
                            element={
                              <Navigate to="/central-dashboard" replace />
                            }
                          />
                          <Route
                            path="/dashboard-central-hub"
                            element={
                              <Navigate to="/central-dashboard" replace />
                            }
                          />
                          <Route
                            path="/enhanced-dashboard"
                            element={
                              <Navigate to="/central-dashboard" replace />
                            }
                          />
                          <Route
                            path="/interactive-dashboard"
                            element={
                              <Navigate to="/central-dashboard" replace />
                            }
                          />
                          <Route
                            path="/unified-dashboard"
                            element={
                              <Navigate to="/central-dashboard" replace />
                            }
                          />
                          <Route
                            path="/central-dashboard"
                            element={
                              <ProtectedRoute moduleName="dashboard">
                                <DashboardRouter />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-agent"
                            element={
                              <ProtectedRoute moduleName="dashboard">
                                <AIAgentPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/command-centre"
                            element={
                              <ProtectedRoute moduleName="dashboard">
                                <DashboardRouter />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/command-center"
                            element={<Navigate to="/command-centre" replace />}
                          />
                          <Route
                            path="/documents"
                            element={
                              <ProtectedRoute moduleName="document-library">
                                <DocumentLibrary />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/compliance"
                            element={
                              <ProtectedRoute moduleName="trust-center">
                                <ComplianceDashboard
                                  enabledFrameworks={
                                    JSON.parse(
                                      localStorage.getItem("enabledModules"),
                                    ) || []
                                  }
                                />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/trust-center"
                            element={
                              <ProtectedRoute moduleName="trust-center">
                                <ComplianceDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/automate-compliance"
                            element={
                              <ProtectedRoute moduleName="trust-center">
                                <AutomateCompliance />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/risk-management"
                            element={
                              <ProtectedRoute moduleName="risk-assessment">
                                <RiskManagement />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-tprm"
                            element={
                              <ProtectedRoute moduleName="third-party-assessment">
                                <TPRM />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-vapt/*"
                            element={
                              <ProtectedRoute moduleName="ai-vapt">
                                <VaptModule />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-crm"
                            element={
                              <ProtectedRoute moduleName="ai-crm">
                                <AiCRM />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-cspm"
                            element={
                              <ProtectedRoute moduleName="ai-cspm">
                                <Ai_CSPM />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-dspm"
                            element={
                              <ProtectedRoute moduleName="dspm">
                                <AI_DSPM />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-lms"
                            element={
                              <ProtectedRoute moduleName="learning-management">
                                <Ai_LMS />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/SOC"
                            element={
                              <ProtectedRoute moduleName="ai-virtual-soc">
                                <VirtualSOC />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/reports"
                            element={
                              <ProtectedRoute moduleName="advanced-reporting">
                                <AiAnalytics />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/integrations"
                            element={
                              <ProtectedRoute moduleName="asset-management">
                                <AssetManagement />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/integrations/:id"
                            element={
                              <ProtectedRoute moduleName="asset-management">
                                <AssetDetailsPage />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/collaboration"
                            element={
                              <ProtectedRoute moduleName="team-collaboration">
                                <TeamCollaboration />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/incident-reporting"
                            element={
                              <ProtectedRoute moduleName="incident-reporting">
                                <IncidentReportingWrapper />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/admin"
                            element={
                              <ProtectedRoute moduleName="administration">
                                <Administration />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/ai-assistant"
                            element={
                              <ProtectedRoute>
                                <AIAssistant />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/external-integrations"
                            element={
                              <ProtectedRoute moduleName="external-integrations">
                                <ExternalIntegrations />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/integration-assessment"
                            element={
                              <ProtectedRoute moduleName="integration-assessment">
                                <IntegrationAssessment />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/advanced-ai"
                            element={
                              <ProtectedRoute>
                                <AdvancedAI />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/security"
                            element={
                              <ProtectedRoute moduleName="administration">
                                <SecurityDashboard />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/settings"
                            element={
                              <ProtectedRoute moduleName="settings">
                                <Administration />
                              </ProtectedRoute>
                            }
                          />
                          <Route
                            path="/enduser-dashboard"
                            element={
                              <Navigate to="/central-dashboard" replace />
                            }
                          />

                          {/* Catch-all route - redirect to landing page for unauthenticated, dashboard for authenticated */}
                          <Route path="*" element={<Navigate to="/" />} />
                        </Routes>
                      </CompanyGuard>
                    </NotificationBridge>
                  </SnackbarProvider>
                </ThemeProvider>
              </AuthProvider>
            </AWSAuthProvider>
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </div>
  );
}

export default App;
